import SectionHeading from "../../components/ui/SectionHeading";
import ScrollReveal from "../../components/animations/ScrollReveal";
import FadeIn from "../../components/animations/FadeIn";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/ui/Button";

export default function About() {
  return (
    <PageWrapper>
      {/* Hero / Page title */}
      <section className="relative overflow-hidden bg-aura-text">
        <div className="absolute inset-0 bg-gradient-to-br from-aura-text via-black to-gold-dark/40" aria-hidden />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-aura-bg via-transparent" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid lg:grid-cols-[minmax(0,1.5fr),minmax(0,1.1fr)] gap-10 lg:gap-16 items-center">
            <FadeIn delay={0.1}>
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-gold-light/60 bg-white/5 px-3 py-1 mb-5">
                  <span className="h-1.5 w-1.5 rounded-full bg-gold-primary" />
                  <span className="font-sans text-[11px] uppercase tracking-[0.24em] text-gold-light">
                    About Aura International
                  </span>
                </span>
                <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-white leading-tight">
                  Excellence in every detail, across every touchpoint.
                </h1>
                <p className="mt-5 font-sans text-sm md:text-base text-gold-light/90 max-w-xl leading-relaxed">
                  Aura International Marketing &amp; Business LLC partners with brands that want more than
                  a one‑off campaign. We bring together marketing, events, interiors, and business support
                  under one roof to deliver experiences that feel premium and work hard for your objectives.
                </p>
                <div className="mt-7 flex flex-wrap gap-4 text-[11px] md:text-xs font-sans text-gold-light/85">
                  <span className="inline-flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                    <span>End‑to‑end support from concept to execution</span>
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                    <span>Trusted by corporates, government, and premium brands</span>
                  </span>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="grid grid-cols-2 gap-4 text-gold-light">
                {[
                  { label: "Projects delivered", value: "500+" },
                  { label: "Brands supported", value: "200+" },
                  { label: "Service lines", value: "4" },
                  { label: "Industries served", value: "15+" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="rounded-sm border border-gold-muted/40 bg-white/5 px-4 py-4 backdrop-blur-sm"
                  >
                    <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-gold-light/80">
                      {item.label}
                    </p>
                    <p className="mt-1 font-serif text-2xl text-white">{item.value}</p>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Who We Are / Intro — same image as homepage */}
      <section className="py-16 md:py-20 bg-aura-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <ScrollReveal delay={0.1} className="order-2 lg:order-1">
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-6">
                Who We Are
              </h2>
              <p className="font-sans text-lg text-aura-text leading-relaxed">
                We combine strategic thinking with flawless execution across event
                management, social media marketing, digital campaigns, interior fit-out,
                and cross-industry expertise in construction, fashion, and beauty.
              </p>
              <p className="mt-6 font-sans text-lg text-aura-text leading-relaxed">
                Our approach is built on understanding your vision, aligning with your
                values, and delivering results that elevate your brand and engage your
                audience.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2} className="order-1 lg:order-2">
              <div className="relative">
                <div className="aspect-[4/3] rounded-sm border border-gold-muted/20 overflow-hidden bg-gold-light/10">
                  <img
                    src={`${process.env.PUBLIC_URL || ""}/Images/Logo.jpeg`}
                    alt="Aura International"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-2 border-gold-primary/40 rounded-sm pointer-events-none" aria-hidden />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 md:py-20 bg-gold-light/10 border-y border-gold-muted/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            overline="Our Story"
            title="Built on vision and execution"
            subtitle="From the beginning, we set out to be the partner that ambitious brands rely on for marketing and business growth."
            align="center"
          />
          <ScrollReveal delay={0.2} className="mt-10 max-w-3xl mx-auto">
            <p className="font-sans text-aura-text leading-relaxed">
              Aura International was founded to bridge the gap between creative
              marketing and measurable business outcomes. We work with enterprises
              and growing brands that demand a premium standard—whether that means
              flawlessly executed events, a standout digital presence, or strategic
              expansion into new markets.
            </p>
            <p className="mt-6 font-sans text-aura-text leading-relaxed">
              Today we serve clients across events, social media, digital marketing,
              interior fit-out, and sectors including construction, fashion, and
              beauty, always with the same commitment: excellence in every detail.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-aura-bg via-aura-text to-aura-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            overline="Mission & Vision"
            title="What drives us"
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-2 gap-10 md:gap-14">
            <ScrollReveal delay={0.1}>
              <div className="relative p-8 md:p-10 rounded-xl border border-gold-light/40 bg-black/40 backdrop-blur-sm text-gold-light">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gold-primary via-gold-light to-gold-primary/70" />
                <h3 className="mt-3 font-serif text-xl md:text-2xl font-semibold text-white mb-3">
                  Our Mission
                </h3>
                <p className="font-sans text-sm md:text-base text-gold-light/90 leading-relaxed">
                  To empower brands with premium marketing and business solutions that create lasting
                  impact. We deliver strategy, creativity, and execution that elevate our clients above
                  the ordinary.
                </p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <div className="relative p-8 md:p-10 rounded-xl border border-gold-light/40 bg-black/40 backdrop-blur-sm text-gold-light">
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-gold-light via-gold-primary to-gold-light/80" />
                <h3 className="mt-3 font-serif text-xl md:text-2xl font-semibold text-white mb-3">
                  Our Vision
                </h3>
                <p className="font-sans text-sm md:text-base text-gold-light/90 leading-relaxed">
                  To be the most trusted partner for brands that refuse to compromise on quality. We
                  aim to set the standard for excellence in marketing, events, and business
                  development across the regions we serve.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 md:py-20 bg-gold-light/10 border-y border-gold-muted/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            overline="Core Values"
            title="What we stand for"
            align="center"
          />
          <div className="mt-14 grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                title: "Strategic",
                desc: "Data-driven and insight-led strategies for lasting impact. We plan before we execute.",
              },
              {
                title: "Premium",
                desc: "Every touchpoint reflects quality and attention to detail. We never cut corners.",
              },
              {
                title: "Partnership",
                desc: "We work alongside you as an extension of your team, invested in your success.",
              },
            ].map((item, i) => (
              <ScrollReveal key={item.title} delay={0.1 + i * 0.1}>
                <div className="relative rounded-xl border border-gold-muted/40 bg-white/80 px-5 py-6 shadow-sm">
                  <div className="absolute -left-4 top-5 h-8 w-8 rounded-full bg-gold-primary text-aura-text font-serif text-base font-semibold flex items-center justify-center">
                    {i + 1}
                  </div>
                  <h3 className="pl-6 font-serif text-xl font-semibold text-aura-text">
                    {item.title}
                  </h3>
                  <p className="mt-3 pl-6 font-sans text-aura-text leading-relaxed text-sm">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-20 bg-aura-text">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            overline="Why Choose Us"
            title="The Aura difference"
            subtitle="We bring a unique combination of expertise, standards, and commitment to every project."
            align="center"
          />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "End-to-end solutions",
                desc: "From strategy to execution, we handle the full journey so you can focus on your business.",
              },
              {
                title: "Industry breadth",
                desc: "Events, digital, social, interior fit-out, construction, fashion, and beauty under one roof.",
              },
              {
                title: "Premium standards",
                desc: "We treat every project with the same level of care and attention to detail.",
              },
              {
                title: "Long-term partnership",
                desc: "We grow with you, adapting our support as your brand evolves.",
              },
            ].map((item, i) => (
              <ScrollReveal key={item.title} delay={0.1 + i * 0.08}>
                <div className="group h-full p-6 rounded-xl border border-gold-primary/35 bg-gradient-to-b from-gold-dark/25 via-gold-dark/10 to-transparent hover:from-gold-primary/35 hover:border-gold-primary/70 transition-all duration-300">
                  <span className="inline-flex w-10 h-10 rounded-full bg-gold-primary text-aura-text font-serif text-lg font-semibold items-center justify-center">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 font-serif text-lg font-semibold text-gold-light">
                    {item.title}
                  </h3>
                  <p className="mt-2 font-sans text-sm text-gold-light/90 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 md:py-20 bg-gold-light/10 border-y border-gold-muted/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            overline="Our Approach"
            title="How we work with you"
            subtitle="A clear, collaborative process designed to deliver results without the hassle."
            align="center"
          />
          <div className="mt-12 max-w-3xl mx-auto">
            <ul className="space-y-6">
              {[
                "We start by understanding your goals, audience, and market so every recommendation is aligned with your vision.",
                "We propose a tailored strategy and plan, with clear milestones and deliverables.",
                "We execute with precision—whether that's an event, a campaign, or a full digital presence—keeping you updated at every stage.",
                "We measure, learn, and refine so your investment continues to pay off.",
              ].map((text, i) => (
                <ScrollReveal key={i} delay={0.1 + i * 0.05}>
                  <li className="relative pl-10">
                    <div className="absolute left-3 top-0 bottom-0 w-px bg-gold-muted/60" aria-hidden />
                    <div className="relative flex items-start gap-3">
                      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gold-primary text-aura-text font-sans text-xs font-semibold flex items-center justify-center mt-0.5">
                        {i + 1}
                      </span>
                      <p className="font-sans text-aura-text leading-relaxed pt-0.5">
                        {text}
                      </p>
                    </div>
                  </li>
                </ScrollReveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-aura-bg">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-aura-text mb-4">
              Ready to work with us?
            </h2>
            <p className="font-sans text-aura-text mb-8">
              Let's discuss how we can help elevate your brand and achieve your goals.
            </p>
            <Button to="/contact" variant="primary">
              Get in Touch
            </Button>
          </FadeIn>
        </div>
      </section>
    </PageWrapper>
  );
}
